import { useEffect, useRef, useState } from 'react'
import { useWorkstationPageContext } from '@/frontend/features/AgentChats/workstationPageContext'
import { onClearDashboardFocus, openDashboardChat } from '@/frontend/features/DashboardRuntime/widgetBridge'
import type { DashboardContext, FrameNavigation, Publication, RuntimeGrant, ViewCommand } from '@/frontend/features/DashboardRuntime/types'

const GRANT_REQUEST = 'stationzero.dashboard.grant-request'
const GRANT = 'stationzero.dashboard.grant'
const THEME = 'stationzero.dashboard.theme'
const NAVIGATE = 'stationzero.dashboard.navigate'

// The embedded renderer has no theme selector of its own: it follows this page.
function hostTheme(): 'light' | 'dark' {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

// The embedded renderer takes this page's look: the resolved design tokens
// (app/frontend/shared/design-tokens.css) travel with the theme message, so
// font, background, cards, text, borders and primary match without the
// runtime copying values. The runtime accepts color functions, lengths and
// font names only.
const TOKENS: Record<string, string> = { background: '--background', card: '--card', foreground: '--foreground', muted: '--muted',
  muted_foreground: '--muted-foreground', border: '--border', primary: '--primary', radius: '--radius' }
function hostTokens(): Record<string, string> {
  const root = getComputedStyle(document.documentElement)
  const tokens = Object.fromEntries(Object.entries(TOKENS).map(([key, name]) => [key, root.getPropertyValue(name).trim()]).filter(([, value]) => value))
  return { ...tokens, font_family: getComputedStyle(document.body).fontFamily }
}

// Where the iframe document comes from. gateway: Rails on this origin, which
// proxies data. direct: the runtime's grant-free renderer shell, which asks
// this page for a short view grant by postMessage and keeps it in memory.
// host_nav=1: this page draws the contents, page steps and filters, so the frame shows content only.
function frameSource(publication: Publication, path: string, page: string | null, filters: Record<string, unknown>) {
  const delivery = publication.delivery
  const base = delivery?.mode === 'direct' ? delivery.view_url : `${path}/view`
  const query = new URLSearchParams({ version: publication.version ?? '', embedded: '1', host_nav: '1', theme: hostTheme(), filters: JSON.stringify(filters) })
  return `${base}${page ? `/pages/${encodeURIComponent(page)}` : ''}?${query}`
}

async function requestGrant(path: string, version: string): Promise<RuntimeGrant | null> {
  const response = await fetch(`${path}/runtime_grant?${new URLSearchParams({ version })}`, {
    headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', redirect: 'error',
  })
  if (!response.ok) return null
  const grant = await response.json() as RuntimeGrant
  return typeof grant.token === 'string' && typeof grant.data_principal_id === 'string' && typeof grant.data_policy_version === 'string' ? grant : null
}

export default function DashboardFrame({ publication, projectId, path, command, navigation, initialPage, onContext, onReady }: {
  publication: Publication; projectId: string; path: string; command?: ViewCommand; navigation?: FrameNavigation; initialPage?: string | null
  onContext?: (context: DashboardContext) => void; onReady?: () => void
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  const [ready, setReady] = useState<string | null>(null)
  const callbacks = useRef({ onContext, onReady })
  callbacks.current = { onContext, onReady }
  const [context, setContext] = useState<DashboardContext>({ page_id: null, filters: {} })
  const version = useRef(publication.version)
  const snapshot = useRef(context)
  snapshot.current = context
  // Messages are accepted from, and posted to, the origin of the framed document only.
  const frameOrigin = publication.delivery?.mode === 'direct' ? publication.delivery.origin : window.location.origin
  // Titles let the chat say which chart it is about; the server resolves the ids again before the agent sees them.
  const pageTitle = publication.schema.pages?.find((item) => item.id === context.page_id)?.title
  const cardTitle = publication.schema.cards?.find((item) => item.id === context.card_id)?.title
  useWorkstationPageContext('dashboard-runtime', { surface: 'dashboard_runtime', project: { id: projectId },
    visualization: { publication_id: publication.id, version: publication.version, ...context,
      page_title: pageTitle ?? null, card_title: cardTitle ?? null } })
  useEffect(() => onClearDashboardFocus(() => setContext((current) => ({ ...current, card_id: null }))), [])

  useEffect(() => {
    if (version.current === publication.version) return
    version.current = publication.version
    // Only replace the iframe revision. The outer widget and draft stay mounted.
    const previous = snapshot.current
    const page = publication.schema.pages?.some((item) => item.id === previous.page_id) ? previous.page_id : null
    const names = new Set(publication.schema.filters?.map((item) => item.name))
    const filters = Object.fromEntries(Object.entries(previous.filters).filter(([name]) => names.has(name)))
    setContext({ page_id: page, filters })
    if (frame.current) frame.current.src = frameSource(publication, path, page, filters)
  }, [publication, path])

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.origin !== frameOrigin || event.source !== frame.current?.contentWindow) return
      const value = event.data
      if (value?.type === GRANT_REQUEST) {
        // Only the revision on screen gets a grant. Nothing is sent on failure:
        // the renderer shows an authorization error after its own timeout.
        if (publication.delivery?.mode !== 'direct' || !publication.version || typeof value.nonce !== 'string' || value.nonce.length > 128) return
        if (value.publication_id !== publication.id || value.version !== publication.version) return
        const source = event.source as Window
        const origin = frameOrigin
        void requestGrant(path, publication.version).then((grant) => {
          if (!grant || frame.current?.contentWindow !== source) return
          source.postMessage({ type: GRANT, nonce: value.nonce, token: grant.token,
            data_principal_id: grant.data_principal_id, data_policy_version: grant.data_policy_version }, origin)
        }).catch(() => undefined)
        return
      }
      if (value?.type === 'stationzero.dashboard.view-ready' && value.publication_id === publication.id && value.version === publication.version) {
        setReady(value.version); callbacks.current.onReady?.(); return
      }
      if (value?.type !== 'stationzero.dashboard.context' || value.publication_id !== publication.id || value.version !== publication.version) return
      if (JSON.stringify(value).length > 12000) return
      const page = publication.schema.pages?.find((item) => item.id === value.page_id)
      const card = publication.schema.cards?.find((item) => item.id === value.card_id)
      if (!page || !value.filters || typeof value.filters !== 'object' || Array.isArray(value.filters)) return
      const names = new Set(publication.schema.filters?.map((item) => item.name))
      const filters = Object.fromEntries(Object.entries(value.filters).filter(([name, item]) => names.has(name) && (item == null || ['string', 'number'].includes(typeof item))))
      setContext({ page_id: page.id, card_id: card?.id, filters })
      callbacks.current.onContext?.({ page_id: page.id, card_id: card?.id, filters })
      if (value.ask === true) openDashboardChat()
    }
    window.addEventListener('message', receive)
    return () => window.removeEventListener('message', receive)
  }, [publication, path, frameOrigin])
  useEffect(() => {
    // Theme changes after load reach the frame by message; the initial one is in its URL.
    const post = () => frame.current?.contentWindow?.postMessage({ type: THEME, theme: hostTheme(), tokens: hostTokens() }, frameOrigin)
    const observer = new MutationObserver(post)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    if (ready) post()
    return () => observer.disconnect()
  }, [ready, frameOrigin])
  useEffect(() => {
    if (ready !== publication.version || !command) return
    frame.current?.contentWindow?.postMessage({ type: 'stationzero.dashboard.apply-view', publication_id: publication.id,
      version: publication.version, ...command }, frameOrigin)
  }, [command, ready, publication.id, publication.version, frameOrigin])
  useEffect(() => {
    // The contents ask for a page (and a topic anchor or new filters) without reloading the frame.
    if (ready !== publication.version || !navigation) return
    frame.current?.contentWindow?.postMessage({ type: NAVIGATE, publication_id: publication.id, version: publication.version,
      page_id: navigation.page_id, section_id: navigation.section_id ?? null, filters: navigation.filters }, frameOrigin)
  }, [navigation, ready, publication.id, publication.version, frameOrigin])
  // src is initialized once; revision changes above preserve page/filter state.
  const [initialSrc] = useState(() => frameSource(publication, path, initialPage ?? null, {}))
  // Below md the page scrolls instead of filling the viewport, so the frame needs its own height.
  return <iframe ref={frame} src={initialSrc} title={publication.title} className="min-h-[75dvh] w-full flex-1 border-0 md:min-h-0" />
}
